import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageCircle, X, Send, Loader2, Brain, Minimize2, Maximize2,
  TrendingUp, TrendingDown, HelpCircle, Sparkles, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quick action suggestions - now with real-time data context
const QUICK_ACTIONS = [
  { label: "Market overview", icon: TrendingUp, query: "Give me a quick market overview with current SPY, QQQ levels and VIX. What's the overall sentiment?" },
  { label: "Check $NVDA", icon: Sparkles, query: "What's NVDA trading at right now? Is it a good entry point for options?" },
  { label: "Volatility play", icon: TrendingDown, query: "Based on current VIX levels, what options strategies make sense right now?" },
  { label: "Risk management", icon: HelpCircle, query: "What position sizing should I use for a $10,000 account trading options?" },
];

const TradeMasterChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "👋 Hey trader! I'm **TradeMaster** with **LIVE market data** access! I can see:\n\n• Real-time stock prices (just mention any ticker like $AAPL or $TSLA)\n• Market indices (SPY, QQQ, VIX)\n• Current market status\n• 52-week ranges & volume\n\nAsk me about specific stocks, options strategies, or market conditions!",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasMarketData, setHasMarketData] = useState(false);
  const [sessionId] = useState(() => `trademaster-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/trademaster/chat`, {
        message: messageText.trim(),
        session_id: sessionId,
        context: {
          recent_messages: messages.slice(-5).map(m => ({
            role: m.role,
            content: m.content
          }))
        }
      });

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: response.data.response,
          timestamp: new Date(),
          hasMarketData: response.data.market_data_included
        };
        setMessages(prev => [...prev, assistantMessage]);
        setHasMarketData(response.data.market_data_included);
      } else {
        throw new Error(response.data.error || 'Failed to get response');
      }
    } catch (error) {
      console.error('TradeMaster error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I apologize, I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAction = (query) => {
    sendMessage(query);
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "👋 Chat cleared! I still have access to **live market data**. What would you like to know?",
      timestamp: new Date()
    }]);
    setHasMarketData(false);
  };

  // Floating button when chat is closed
  if (!isOpen) {
    return (
      <div className="fixed bottom-20 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
          data-testid="trademaster-open-btn"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
        <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
          AI
        </div>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div className="fixed bottom-20 right-6 z-50">
        <div 
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full px-4 py-2 shadow-lg cursor-pointer flex items-center space-x-2 hover:shadow-xl transition-all"
          onClick={() => setIsMinimized(false)}
        >
          <Brain className="h-5 w-5" />
          <span className="font-medium">TradeMaster</span>
          <Badge className="bg-white/20 text-white text-xs">
            {messages.length - 1} msgs
          </Badge>
        </div>
      </div>
    );
  }

  // Full chat window
  return (
    <div className="fixed bottom-20 right-6 z-50 w-96 h-[550px] flex flex-col bg-white rounded-2xl shadow-2xl border overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="bg-white/20 rounded-full p-1.5">
            <Brain className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold flex items-center">
              TradeMaster AI
              <span className="ml-2 flex items-center text-xs bg-green-500/30 text-green-200 px-1.5 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse"></span>
                LIVE
              </span>
            </h3>
            <p className="text-xs text-white/80">Real-Time Market Data</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={clearChat}
            className="h-8 w-8 text-white hover:bg-white/20"
            title="Clear chat"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMinimized(true)}
            className="h-8 w-8 text-white hover:bg-white/20"
            title="Minimize"
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            className="h-8 w-8 text-white hover:bg-white/20"
            title="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((message, idx) => (
            <div
              key={idx}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-br-md'
                    : message.isError
                      ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
              >
                <div className="text-sm whitespace-pre-wrap leading-relaxed">
                  {message.content.split('**').map((part, i) => 
                    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                  )}
                </div>
                <div className={`text-xs mt-1 ${message.role === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center space-x-2">
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                  <span className="text-sm text-gray-500">TradeMaster is thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Quick Actions */}
      {messages.length <= 2 && (
        <div className="px-4 py-2 border-t bg-gray-50 flex-shrink-0">
          <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_ACTIONS.map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => handleQuickAction(action.query)}
                disabled={isLoading}
                className="text-xs h-7 px-2"
              >
                <action.icon className="h-3 w-3 mr-1" />
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-3 border-t bg-white flex-shrink-0">
        <div className="flex items-center space-x-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask TradeMaster anything..."
            disabled={isLoading}
            className="flex-1 rounded-full border-gray-200 focus:border-indigo-400"
            data-testid="trademaster-input"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || isLoading}
            className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 p-0"
            data-testid="trademaster-send-btn"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TradeMasterChat;
